import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Environment from './MapElements/Environment';
import { ITile } from '../../types/types';
import { Container as PixiContainer } from 'pixi.js';
import { Container, Sprite, Text, useTick } from '@pixi/react';
import {
  tileWidth,
  tileHeight,
  floorTilesets,
  DEFAULT_TILESET_MAPPING,
} from '../../data/tileset.config';
import { mapService } from '../../services/MapService';
import { AimingOverlay } from './AimingOverlay';
import { dungeonService } from '../../services/DungeonService';
import {
  onDungeonCompleted,
  onBossFirstKill,
} from '../../services/ContentUnlockService';
import { hubService } from '../../services/HubService';
import { animationService } from '../../services/AnimationService';
import { projectileService } from '../../services/ProjectileService';
import { zoneEffectService } from '../../services/ZoneEffectService';
import { dynamicPlacementService } from '../../services/DynamicPlacementService';
import { MonsterEntity } from '../../entities/MonsterEntity';
import { PlayerEntity } from '../../entities/PlayerEntity';
import { Texture } from 'pixi.js';
import { useGameStore } from '../../store/gameSlice';
import { useSpritesStore } from '../../store/spritesSlice';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { AffectedTilesOverlayAlone } from './AffectedTilesOverlayAlone';
import { SpellAnimationsRenderer } from './SpellAnimationsRenderer';
import { ProjectileRenderer } from './ProjectileRenderer';
import { EffectsPreloader } from '../EffectsPreloader';
import EntityRenderer from './EntityRenderer';
import { useDungeonInput } from '../../hooks/useDungeonInput';
import { GroundItemsRenderer } from './GroundItemsRenderer';
import { saveService } from '../../services/SaveService';
import { multiplayerService } from '../../services/MultiplayerService';
import { LocationRenderer } from './LocationRenderer';
import { closeInterface } from '../../services/InterfaceService';
import { recoveryService } from '../../services/RecoveryService';
import { SocketService } from '../../services/SocketService';
import { useFloorTransitionStore } from '../../store/floorTransitionSlice';
import {
  DUNGEON_REGISTRY,
  DEFAULT_DUNGEON_ID,
} from '../../data/dungeons.config';
import { resolveFloor } from '../../utils/floor-resolver.utils';
import { DungeonRunConfig } from '../../types/dungeon.types';
import { bundleLoaderService } from '../../services/BundleLoaderService';
import { preloadBulletSheets } from '../../services/LazySpritesheetService';
import { getZIndex } from '../../utils/zindex.utils';
import { audioService } from '../../services/AudioService';
import { cinematicService } from '../../services/CinematicService';
import {
  registerCinematicScene,
  clearCinematicScene,
  CinematicSceneAdapter,
  CinematicActor,
} from '../../cinematic/sceneAdapter';
import { useCinematicStore } from '../../store/cinematicSlice';
import { ActorRef } from '../../data/cinematics/types';
import { DungeonEntity } from '../../entities/DungeonEntity';
import { netService } from '../../services/NetService';
import { applyIntentOnHost, resetMoveTracking } from '../../net/inputSync';
import { ReplicationHost } from '../../net/replicationHost';
import { ReplicationClient } from '../../net/replicationClient';
import type { FloorMessage } from '../../net/replication';
import { v4 as uuidv4 } from 'uuid';
import {
  REQUEST_FLOOR,
  makeFloorTransitionStart,
  makeTileMutation,
  makeWorldUpdate,
  type FloorTransitionStartMsg,
  type ReviveRequestMsg,
  type WorldUpdateMsg,
  type PlayerDiedMsg,
  type DeathResolutionMsg,
  type PlayerRevivedMsg,
  type RunWipeMsg,
  type MonsterKilledMsg,
  type LocationFireMsg,
  type ThrowItemMsg,
  type CommonItemAddMsg,
  type CommonPickupRequestMsg,
  type CommonItemRemoveMsg,
  makeCommonItemAdd,
  makeCommonItemRemove,
} from '../../net/sessionEvents';
import { LOCATION_REGISTRY } from '../../data/locations.config';
import { makePlayerLoadout, LoadoutGate } from '../../net/loadoutSync';
import {
  getClientToken,
  getBoundPlayerId,
  reconnectRegistry,
  makeReconnectAccept,
  clearCoopResume,
  getCoopGround,
  clearCoopGround,
  RECONNECT_REJECT,
  type ReconnectRequestMsg,
} from '../../net/reconnect';
import { captureLoadoutSnapshot } from '../../services/LoadoutSourceService';
import { useCoopRunStore } from '../../store/coopRunSlice';
import { useReconnectStore } from '../../store/reconnectSlice';
import { useNetSessionStore } from '../../store/netSessionSlice';
import { useCharacterProgressionStore } from '../../store/characterProgressionSlice';
import { coopDeathHost } from '../../services/CoopDeathHost';
import {
  coopDeathClient,
  resetCoopDeath,
} from '../../services/CoopDeathClient';
import { useSpectatorStore } from '../../store/spectatorSlice';

const Dungeon = () => {
  const isLoaded = useSpritesStore((s) => s.isLoaded);
  const lockCameraPosition = useGameStore((s) => s.lockCameraPosition);
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const screenWidth = useGameStore((s) => s.screenWidth);
  const screenHeight = useGameStore((s) => s.screenHeight);
  const setCameraPosition = useGameStore((s) => s.setCameraPosition);
  const entityTick = useDevtoolStore((s) => s.entityTick);
  const setEntityTick = useDevtoolStore((s) => s.setEntityTick);
  const netEpoch = useNetSessionStore((s) => s.epoch);
  const currentFloor = useGameStore((s) => s.currentFloor);
  const currentDungeonId = useGameStore((s) => s.currentDungeonId);
  const dungeonRunConfig = useGameStore((s) => s.dungeonRunConfig);
  const activeRunConfig: DungeonRunConfig = useMemo(() => {
    const id = currentDungeonId ?? DEFAULT_DUNGEON_ID;
    if (dungeonRunConfig && dungeonRunConfig.dungeonId === id) {
      return dungeonRunConfig;
    }
    const def = DUNGEON_REGISTRY[id] ?? DUNGEON_REGISTRY[DEFAULT_DUNGEON_ID];
    return { dungeonId: def.id, ...def.initialConfig };
  }, [dungeonRunConfig, currentDungeonId]);
  const currentTilesetKey = resolveFloor(activeRunConfig, currentFloor).tileset;

  const CAMERA_LERP_SPEED = 0.12;
  const smoothCameraRef = useRef({ x: 0, y: 0 });
  const cinematicZoomRef = useRef(1);
  const cameraContainerRef = useRef<PixiContainer | null>(null);
  const replicationHostRef = useRef<ReplicationHost | null>(null);
  const replicationClientRef = useRef<ReplicationClient | null>(null);

  const setGameplay = useGameStore((s) => s.setGameplay);

  const [floorTiles, setFloorTiles] = useState<ITile[]>([]);
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
  const [, setTick] = useState(0);

  const applyCameraTransform = useCallback(() => {
    const c = cameraContainerRef.current;
    if (!c) {
      return;
    }
    const { screenWidth, screenHeight, zoomFactor } = useGameStore.getState();
    const smooth = smoothCameraRef.current;
    const z = cinematicZoomRef.current;
    const pivotX = smooth.x * zoomFactor;
    const pivotY = -smooth.y * zoomFactor;
    c.pivot.set(pivotX, pivotY);
    c.scale.set(z);
    c.position.set(screenWidth / 2, screenHeight / 2);
  }, []);

  const applyFloorSwap = useCallback(
    (floorNumber: number, existingPlayer?: PlayerEntity) => {
      const {
        tiles,
        width,
        height,
        spawnX,
        spawnY,
        floorConfig,
        rooms,
        placedLocations,
      } = dungeonService.generateFloor(floorNumber, existingPlayer);

      // Stale projectiles must not survive a floor change.
      projectileService.reset();

      // No-op (auto cross-fade) if the same biome track is already playing.
      audioService.playMusic(floorConfig.biome.musicId);

      setFloorTiles(tiles);
      setMapSize({ width, height });
      mapService.setRooms(rooms);

      saveService.setMapData(
        floorConfig.style,
        width,
        height,
        tiles,
        rooms,
        placedLocations,
      );

      smoothCameraRef.current = {
        x: spawnX * tileWidth,
        y: spawnY * tileHeight,
      };
      setCameraPosition({ x: spawnX * tileWidth, y: spawnY * tileHeight });
      // The render loop is paused during a transition, so `useTick` won't run
      // to position the camera container — apply it now so the floor is framed
      // on the player as soon as the cover fades out.
      applyCameraTransform();

      // Reveal fog-of-war around the spawn tile (otherwise nothing is visible
      // until the player's first move triggers PlayerEntity.onPositionChange).
      const spawnedPlayer = dungeonService.getPlayer();
      if (spawnedPlayer) {
        mapService.revealAround(spawnX, spawnY, spawnedPlayer.stats.viewRange);
        // Also pick up anything that ended up on or next to the spawn tile.
        dungeonService.inventoryService.pickupItemsInRange(
          spawnedPlayer,
          spawnedPlayer.stats.pickupReach,
        );
      }

      // Test arena: no fog of war — the whole map is revealed for spell testing.
      if (floorConfig.biome.generation === 'test-arena') {
        mapService.revealAll();
      }

      // Commit the new floor's tiles to mapService NOW (the `floorTiles` React
      // effect that normally does this runs after this callback), so the beacon
      // respawn's `isSpawnableCell` checks the new floor and not the old one's
      // walls.
      mapService.setMapTiles(tiles);
      coopDeathHost.respawnBeaconsForFloor();

      const floorCount = dungeonService.getCurrentDungeonFloorCount();
      if (floorNumber < floorCount) {
        const nextBiomeId = resolveFloor(
          dungeonService.ensureRunConfig(),
          floorNumber + 1,
        ).biome.id;
        bundleLoaderService.prefetchBundle('biome:' + nextBiomeId);
      }
      // Initial checkpoint on floor entry: autosave otherwise fires only on the
      // first move, so a crash/F5 during loading (host) would leave no run-save
      // and resume would fall back to the solo hub. (No-op for clients.)
      saveService.save();
    },
    [setCameraPosition, applyCameraTransform],
  );

  useEffect(() => {
    const makeActor = (entity: DungeonEntity): CinematicActor => ({
      getPosition: () => ({ x: entity.absoluteX, y: entity.absoluteY }),
      setPosition: (p) => {
        entity.absoluteX = p.x;
        entity.absoluteY = p.y;
        entity.markDirty();
      },
      setFacing: (d) => entity.face(d),
      playWalk: () => {
        entity.currentSpriteName = 'run';
        entity.markDirty();
      },
      playIdle: () => {
        entity.currentSpriteName = 'idle';
        entity.markDirty();
      },
    });

    const adapter: CinematicSceneAdapter = {
      camera: {
        getPosition: () => ({ ...smoothCameraRef.current }),
        setPosition: (p) => {
          smoothCameraRef.current.x = p.x;
          smoothCameraRef.current.y = p.y;
          useGameStore.getState().setCameraPosition({ x: p.x, y: p.y });
          applyCameraTransform();
        },
        getZoom: () => cinematicZoomRef.current,
        setZoom: (z) => {
          cinematicZoomRef.current = z;
          applyCameraTransform();
        },
        setFollow: (actor: ActorRef | null) => {
          useGameStore.getState().setLockCameraPosition(actor === null);
          if (actor !== null) {
            dungeonService.requestCameraSnap();
          }
        },
      },
      getActor: (ref) => {
        if (ref === 'player') {
          const p = dungeonService.getPlayer();
          return p ? makeActor(p) : null;
        }
        const e = dungeonService
          .getEntities()
          .find((x) => x.id === ref.entityId);
        return e ? makeActor(e) : null;
      },
    };

    registerCinematicScene(adapter);
    return () => clearCinematicScene(adapter);
  }, [applyCameraTransform]);

  // In-dungeon floor change: the cover fades in over the CURRENT floor, the
  // swap runs once it is opaque, then it fades out over the new floor. The
  // fade-in / swap / fade-out timing is owned by `FloorTransition`.
  // `floorTransitionSlice` calls swap synchronously (no async path), so the
  // bundle must be ready before `start` is called.
  const startFloorChange = useCallback(
    (floorNumber: number, existingPlayer?: PlayerEntity) => {
      const biomeId = resolveFloor(
        dungeonService.ensureRunConfig(),
        floorNumber,
      ).biome.id;
      void bundleLoaderService.ensureBundle('biome:' + biomeId).then(() => {
        useFloorTransitionStore
          .getState()
          .start(floorNumber, 'swap', () =>
            applyFloorSwap(floorNumber, existingPlayer),
          );
        // Replicate the transition so a client raises its own cover and stays
        // input-blocked for the FULL host transition (the host sim is frozen
        // meanwhile). Sent before the floor message (reliable+ordered) so the
        // cover is up when the new floor arrives.
        if (netService.isHost()) {
          netService.session?.broadcast(
            'reliable',
            makeFloorTransitionStart(floorNumber),
          );
        }
      });
    },
    [applyFloorSwap],
  );

  // Client floor entry: the client never generates a floor — it applies the
  // host's replicated floor instead. The biome bundle must be loaded first so
  // tile/entity sprites resolve (the client skips the transition flow that
  // normally ensures it). Fog of war is fully revealed since the client runs no
  // movement sim to drive `revealAround`.
  const applyReplicatedFloor = useCallback((floor: FloorMessage) => {
    void bundleLoaderService.ensureBundle('biome:' + floor.biomeId).then(() => {
      dungeonService.reset();
      if (floor.floor !== undefined) {
        useGameStore.getState().setCurrentFloor(floor.floor);
      }
      projectileService.reset();
      dungeonService.localPlayerId =
        getBoundPlayerId() ??
        netService.session?.localPeerId() ??
        floor.localPlayerId;
      resetMoveTracking();
      mapService.setMapTiles(floor.tiles);
      mapService.setRooms(floor.rooms);
      dungeonService.locationService.setPlacedLocations(floor.placedLocations);
      // Restore consumption so a (re)joining client's prompts match the host's
      // authority — without it, already-looted once-per-player locations show a
      // prompt that the host silently rejects (cf. docs/multiplayer.md § Locations).
      dungeonService.locationService.restoreState(
        floor.usedLocations ?? [],
        floor.destroyedLocations ?? [],
      );
      dungeonService.locationService.restoreUsedByPlayer(
        floor.usedByPlayer ?? {},
      );
      // The minimap reads saveService.getMapData() — populate it on the client.
      saveService.setMapData(
        floor.style,
        floor.width,
        floor.height,
        floor.tiles,
        floor.rooms,
        floor.placedLocations,
      );
      setFloorTiles(floor.tiles);
      setMapSize({ width: floor.width, height: floor.height });
      mapService.revealAll();
      dungeonService.requestCameraSnap();
      // The client runs no generateFloor()/initialize(); enable input explicitly
      // so the predicted local player can act once it arrives via snapshots —
      // unless a replicated floor-transition cover is up, which owns the input
      // gate and lifts it only when the transition ends (full host window).
      if (!useFloorTransitionStore.getState().active) {
        dungeonService.allowPlayerInput = true;
      }
      // Warm the tiny bullet sheets so a replicated projectile doesn't flash a
      // placeholder on its first appearance (the client never fires locally).
      preloadBulletSheets();
      const savedGround = getCoopGround();
      if (
        savedGround &&
        floor.floor !== undefined &&
        savedGround.floor === floor.floor
      ) {
        for (const gi of savedGround.items) {
          dungeonService.inventoryService.dropItem(
            gi.x,
            gi.y,
            gi.itemId,
            gi.quantity,
            false,
            'possessed',
          );
        }
        clearCoopGround();
      }
      for (const ci of floor.commonItems ?? []) {
        dungeonService.inventoryService.addCommonItemRaw({
          id: ci.id,
          itemId: ci.itemId,
          x: ci.x,
          y: ci.y,
          quantity: ci.quantity,
          mode: 'common',
          dropperId: ci.dropperId,
          armed: true,
        });
      }
    });
  }, []);

  // Wire the replication endpoints once per role. The role is fixed before this
  // component mounts (the harness/lobby sets it on host/join), so a one-shot
  // mount effect is enough. A layout effect (not passive) so the host endpoint
  // exists before the floor-generation layout effect below runs `sendFloor`.
  useLayoutEffect(() => {
    const session = netService.session;
    if (!session) {
      return undefined;
    }
    if (netService.isHost()) {
      const host = new ReplicationHost(session);
      replicationHostRef.current = host;
      reconnectRegistry.reset();
      // Clear stale remote loadouts ONCE at run-start, before the gate collects.
      // dungeonService.reset() (run per floor inside generateFloor) deliberately
      // no longer clears them — it would race the gate and wipe a just-received
      // loadout on the first, bundle-gated floor. (resumeHost re-seeds via restore.)
      dungeonService.remoteLoadouts.clear();
      useCoopRunStore.getState().setPhase('loading');
      // Party members connected from the shared hub before launch: onPeerJoin
      // already fired in the hub (no ReplicationHost then), so the gate will
      // spawn them once their loadout arrives.
      for (const peerId of session.peers()) {
        dungeonService.spawnRemotePlayer(
          reconnectRegistry.resolvePlayerId(peerId),
        );
      }
      const gate = new LoadoutGate(session.peers());
      const offJoin = session.onPeerJoin(() => {
        // Party is frozen in dungeon: no fresh spawn. Launch-party spawns go
        // through the LoadoutGate's player-loadout handler; a reconnecting peer
        // is routed by the reconnect-request handler below. Just resend the
        // floor so the (re)joining peer is in sync.
        host.resendFloor();
        window.setTimeout(() => host.resendFloor(), 500);
      });
      const offLeave = session.onPeerLeave((peerId) => {
        // Keep the route: a deliberate `leave-run` racing this drop must still
        // resolve to the avatar id. A stale route for a gone peer is harmless
        // (that peerId never sends again); `forgetPlayer` purges it on removal.
        const playerId = reconnectRegistry.resolvePlayerId(peerId);
        const avatar = dungeonService
          .getPlayers()
          .find((p) => p.id === playerId);
        if (avatar) {
          // Keep the avatar in the sim: immobile (no more intents) and a valid
          // monster target. Reclaimable via token for the whole run.
          avatar.disconnected = true;
          avatar.clearPendingMoves();
        }
      });
      const offFloor = session.on('request-floor', () => host.resendFloor());
      const offRevive = session.on('revive-request', (from, m) => {
        coopDeathHost.handleReviveRequest(
          from,
          (m as ReviveRequestMsg).deadPlayerId,
        );
      });
      const offDeath = session.on('death-resolution', (_from, m) => {
        const d = m as DeathResolutionMsg;
        coopDeathHost.handleDeathResolution(d.playerId, d.cell, d.revived);
      });
      const offThrow = session.on('throw-item', (from, m) => {
        const d = m as ThrowItemMsg;
        const dropperId = reconnectRegistry.resolvePlayerId(from);
        const item = {
          id: uuidv4(),
          itemId: d.itemId,
          x: d.x,
          y: d.y,
          quantity: d.quantity,
          mode: 'common' as const,
          dropperId,
        };
        dungeonService.inventoryService.addCommonItemRaw(item);
        session.broadcast(
          'reliable',
          makeCommonItemAdd(item.id, d.itemId, d.quantity, d.x, d.y, dropperId),
        );
      });
      const offPickupReq = session.on('common-pickup-request', (from, m) => {
        const d = m as CommonPickupRequestMsg;
        const playerId = reconnectRegistry.resolvePlayerId(from);
        const item = dungeonService.inventoryService.getCommonItemById(d.id);
        if (!item) {
          return;
        }
        if (item.dropperId === playerId && item.armed !== true) {
          return;
        }
        dungeonService.inventoryService.applyCommonItemRemoval(d.id, playerId);
        session.broadcast('reliable', makeCommonItemRemove(d.id, playerId));
      });
      const offReconnect = session.on('reconnect-request', (from, m) => {
        const token = (m as ReconnectRequestMsg).token;
        const playerId = reconnectRegistry.playerForToken(token);
        if (!playerId) {
          session.send(from, 'reliable', RECONNECT_REJECT);
          return;
        }
        const avatar = dungeonService
          .getPlayers()
          .find((p) => p.id === playerId);
        const downed = coopDeathHost.isDownedForReconnect(playerId);
        // Valid reclaim target = a live avatar held for it, OR a downed player
        // (no live entity, revivable). Otherwise the token maps to nothing.
        if (!avatar && !downed) {
          session.send(from, 'reliable', RECONNECT_REJECT);
          return;
        }
        reconnectRegistry.route(from, playerId);
        if (avatar) {
          avatar.disconnected = false;
        }
        host.resendFloor();
        session.send(from, 'reliable', makeReconnectAccept(playerId, downed));
      });
      const offLeaveRun = session.on('leave-run', (from) => {
        const playerId = reconnectRegistry.resolvePlayerId(from);
        reconnectRegistry.forgetPlayer(playerId);
        dungeonService.removeEntity(playerId);
        dungeonService.runPlayerIds.delete(playerId);
        coopDeathHost.handlePlayerLeftRun(playerId);
      });
      const offIntents = session.onMessage((from, m) =>
        applyIntentOnHost(from, m),
      );
      return () => {
        offJoin();
        offLeave();
        offFloor();
        offRevive();
        offDeath();
        offThrow();
        offPickupReq();
        offReconnect();
        offLeaveRun();
        offIntents();
        gate.dispose();
        reconnectRegistry.reset();
        useCoopRunStore.getState().setPhase('idle');
        replicationHostRef.current = null;
      };
    }
    if (netService.isClient()) {
      const client = new ReplicationClient(applyReplicatedFloor);
      replicationClientRef.current = client;
      const REPLICATED = [
        'floor',
        'snapshot',
        'projectile-spawn',
        'projectile-despawn',
        'vfx',
        'vfx-despawn',
        'damage-number',
        'preview-add',
        'preview-remove',
        'cast-rejected',
      ] as const;
      const offReplicated = REPLICATED.map((t) =>
        session.on(t, (from, m) => client.handle(from, m)),
      );
      // A reconnect / boot-resume reclaims an already-running run (a bound avatar
      // id is set before this effect re-runs): the host's run-start was broadcast
      // before we rejoined, so 'loading' would never clear. Enter 'running'
      // directly. A fresh launch (no bound id) still waits for run-start through
      // the start barrier.
      useCoopRunStore
        .getState()
        .setPhase(getBoundPlayerId() ? 'running' : 'loading');
      session.broadcast('reliable', REQUEST_FLOOR);
      session.broadcast(
        'reliable',
        makePlayerLoadout(captureLoadoutSnapshot(), getClientToken()),
      );
      const offRunStart = session.on('run-start', () => {
        useCoopRunStore.getState().setPhase('running');
      });
      const offFloorTransition = session.on(
        'floor-transition-start',
        (_from, m) => {
          // Mirror the host's transition: 'enter' cover (floor swaps under it via
          // the floor message) that self-ends after the standard duration, holding
          // input blocked while the host's shared sim is frozen.
          useFloorTransitionStore
            .getState()
            .start((m as FloorTransitionStartMsg).floor, 'enter');
        },
      );
      const offWorldUpdate = session.on('world-update', (_from, m) => {
        const upd = m as WorldUpdateMsg;
        for (const d of upd.locations ?? []) {
          dungeonService.locationService.applyLocationDelta(d);
        }
        if (upd.tiles && upd.tiles.length > 0) {
          mapService.applyTileDeltas(upd.tiles);
        }
      });
      const offPlayerDied = session.on('player-died', (_from, m) => {
        const d = m as PlayerDiedMsg;
        coopDeathClient.onPlayerDied(d.playerId, d.cell);
      });
      const offPlayerRevived = session.on('player-revived', (_from, m) => {
        if ((m as PlayerRevivedMsg).playerId === dungeonService.localPlayerId) {
          useSpectatorStore.getState().setDowned(false);
          // The host re-created the entity with lastInputSeq=0; realign the
          // client's move-seq so prediction isn't capped (would block moves).
          resetMoveTracking();
        }
      });
      const offRunWipe = session.on('run-wipe', (_from, m) => {
        netService.session?.setPhase('hub');
        clearCoopResume();
        clearCoopGround();
        resetCoopDeath();
        SocketService.clearRunModifiers();
        recoveryService.applyDefeatRecovery();
        saveService.clearRunSave();
        closeInterface();
        // Tell the client WHY it was pulled back to the hub instead of yanking it
        // there silently (a team wipe vs the host deliberately abandoning).
        useNetSessionStore
          .getState()
          .setNotice(
            (m as RunWipeMsg).reason === 'abandon'
              ? 'L’hôte a abandonné la run.'
              : 'Votre équipe a été vaincue.',
          );
        void hubService.enterHub('from-defeat');
      });
      const offMonsterKilled = session.on('monster-killed', (_from, m) => {
        const d = m as MonsterKilledMsg;
        const firstKill = !useCharacterProgressionStore
          .getState()
          .bossRewardsClaimed.includes(d.configId);
        const localMultiplier =
          d.killerId === dungeonService.localPlayerId
            ? d.richDropMultiplier
            : 1;
        dungeonService.monsterSpawningService.rollMonsterLootSpread(
          d.configId,
          d.positions,
          d.isElite,
          localMultiplier,
          d.isBoss,
          firstKill,
        );
        // The client runs no death(): apply its own XP + bestiary from the
        // replicated event, with its LOCAL level (full per-player XP).
        dungeonService.applyKillProgression(d.configId, d.level, d.isElite);
        // Boss/mini-boss: grant the non-spell first-kill rewards (major/minor/
        // sketch + showcase) on this client's OWN first kill. onBossFirstKill is
        // idempotent (claims via bossRewardsClaimed) and gates the grimoire
        // already rolled above; the spell reward stays the dropped grimoire.
        if (d.isBoss || d.isMiniBoss) {
          onBossFirstKill(d.configId);
        }
      });
      const offLocationFire = session.on('location-fire', (_from, m) => {
        const d = m as LocationFireMsg;
        if (
          d.targetPlayerId !== undefined &&
          d.targetPlayerId !== dungeonService.localPlayerId
        ) {
          return;
        }
        const loc = dungeonService.locationService
          .getPlacedLocations()
          .find((l) => l.id === d.locationId && l.x === d.x && l.y === d.y);
        if (!loc) {
          return;
        }
        const locDef = LOCATION_REGISTRY[loc.id];
        const effects = locDef?.effects ?? [];
        if (effects.some((e) => e.handler === 'markPoiUsed')) {
          if (locDef?.oncePerPlayer && d.targetPlayerId) {
            dungeonService.locationService.markUsedByPlayer(
              loc,
              d.targetPlayerId,
            );
          } else {
            dungeonService.locationService.markLocationUsed(loc);
          }
        }
        if (effects.some((e) => e.handler === 'triggerPoiBreakAnimation')) {
          dungeonService.locationService.triggerLocationBreak(loc);
        }
        const player = dungeonService.getPlayer();
        if (player) {
          dungeonService.locationService.runLocationOwnerEffects(loc, player);
        }
      });
      const offCommonAdd = session.on('common-item-add', (_from, m) => {
        const d = m as CommonItemAddMsg;
        dungeonService.inventoryService.addCommonItemRaw({
          id: d.id,
          itemId: d.itemId,
          x: d.x,
          y: d.y,
          quantity: d.quantity,
          mode: 'common',
          dropperId: d.dropperId,
        });
      });
      const offCommonRemove = session.on('common-item-remove', (_from, m) => {
        const d = m as CommonItemRemoveMsg;
        dungeonService.inventoryService.applyCommonItemRemoval(
          d.id,
          d.byPlayerId,
        );
      });
      return () => {
        offReplicated.forEach((o) => o());
        offRunStart();
        offFloorTransition();
        offWorldUpdate();
        offPlayerDied();
        offPlayerRevived();
        offRunWipe();
        offMonsterKilled();
        offLocationFire();
        offCommonAdd();
        offCommonRemove();
        useCoopRunStore.getState().setPhase('idle');
        replicationClientRef.current = null;
      };
    }
    return undefined;
  }, [applyReplicatedFloor, netEpoch]);

  // Layout effect (not passive): the floor is generated and the cover screen is
  // activated BEFORE the first paint, so the freshly entered dungeon is never
  // visible for a frame before the transition covers it.
  useLayoutEffect(() => {
    // The client waits for the host's `floor` message (applyReplicatedFloor)
    // instead of generating or restoring a floor.
    if (netService.isClient()) {
      return;
    }
    const saved = saveService.load();

    void (async () => {
      if (saved) {
        dungeonService.reset();
        useGameStore.getState().setCurrentFloor(saved.currentFloor);
        if (saved.currentDungeonId) {
          useGameStore.getState().setCurrentDungeonId(saved.currentDungeonId);
        }
        // Restore the mutable run config (alt paths, etc.). If absent (older
        // save), ensureRunConfig rebuilds it from the registry on next access.
        useGameStore
          .getState()
          .setDungeonRunConfig(saved.dungeonRunConfig ?? null);
        // Level / XP are intentionally not restored from the save: progression is
        // persisted per profile via `useCharacterProgressionStore` (its own localStorage).
        const { tiles, width, height } = saveService.restoreFromSave(saved);
        const mapData = saveService.getMapData();
        if (mapData?.placedLocations) {
          dungeonService.locationService.setPlacedLocations(
            mapData.placedLocations,
          );
          dungeonService.locationService.restoreState(
            saved.map.usedLocationKeys ?? [],
            saved.map.destroyedLocationKeys ?? [],
          );
          dungeonService.locationService.restoreUsedByPlayer(
            saved.usedByPlayer ?? {},
          );
          dungeonService.restoreRunUsedLocationIds(
            saved.runUsedLocationIds ?? [],
          );
        }

        if (saved.coop && netService.isHost()) {
          dungeonService.localPlayerId =
            netService.session?.localPeerId() ?? dungeonService.localPlayerId;
          saveService.restoreCoopState(
            saved,
            multiplayerService.getDepartedTokens(),
          );
          // Players who quit for good while we were down are dropped silently;
          // the run continues (we're still alive). Tell the host who's missing.
          const departed = multiplayerService.getDepartedTokens();
          const leftCount = (saved.coopPlayers ?? []).filter((p) =>
            departed.has(p.token),
          ).length;
          if (leftCount > 0) {
            useNetSessionStore
              .getState()
              .setNotice(
                leftCount > 1
                  ? `${leftCount} joueurs ont quitté la partie.`
                  : 'Un joueur a quitté la partie.',
              );
          }
        }

        setFloorTiles(tiles);
        setMapSize({ width, height });
        if (mapData?.rooms) {
          mapService.setRooms(mapData.rooms);
        }

        const player = dungeonService.getPlayer();
        if (player) {
          smoothCameraRef.current = {
            x: player.absoluteX,
            y: player.absoluteY,
          };
          setCameraPosition({ x: player.absoluteX, y: player.absoluteY });
          applyCameraTransform();
          mapService.revealAround(
            player.positionX,
            player.positionY,
            player.stats.viewRange,
          );
        }
        // Restore the spawn context that `generateFloor` normally sets —
        // otherwise biome-driven effects (alarm trap `spawnBiomeMonsters`,
        // respawns) and the music fall back to the wrong/default dungeon.
        // `ensureRunConfig` resolves from the just-restored run config (or
        // rebuilds it from the saved dungeon id).
        const savedFloorConfig = dungeonService.resolveFloorInfo(
          saved.currentFloor,
        );
        dungeonService.setFloorConfig(savedFloorConfig);
        dungeonService.monsterSpawningService.setSpawnerEnabled(
          savedFloorConfig.floorType !== 'boss',
        );
        audioService.playMusic(savedFloorConfig.biome.musicId);
        await bundleLoaderService.ensureBundle(
          'biome:' + savedFloorConfig.biome.id,
        );
        useFloorTransitionStore.getState().start(saved.currentFloor, 'enter');
      } else {
        const biomeId = resolveFloor(dungeonService.ensureRunConfig(), 1).biome
          .id;
        await bundleLoaderService.ensureBundle('biome:' + biomeId);
        applyFloorSwap(1);
        useFloorTransitionStore.getState().start(1, 'enter');
      }
    })();
  }, [applyFloorSwap, setCameraPosition, applyCameraTransform]);

  useEffect(() => {
    mapService.setMapTiles(floorTiles);
  }, [floorTiles]);

  // Host: broadcast the floor once per floor (keyed on mapSize, which changes on
  // floor entry but not on fog-of-war reveals). Covers both the fresh-gen and
  // save-restore entry paths. Runs after the setMapTiles effect above, so
  // `getMapTiles()` is populated.
  useEffect(() => {
    if (!netService.isHost() || !replicationHostRef.current) {
      return;
    }
    if (mapSize.width === 0) {
      return;
    }
    const tiles = mapService.getMapTiles();
    if (tiles.length === 0) {
      return;
    }
    const fc = resolveFloor(activeRunConfig, currentFloor);
    replicationHostRef.current.sendFloor(
      tiles,
      mapSize.width,
      mapSize.height,
      fc.tileset,
      fc.biome.id,
      dungeonService.localPlayerId ?? '',
      dungeonService.locationService.getPlacedLocations(),
      mapService.getRooms(),
      fc.style,
    );
    const session = netService.session;
    if (session) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[floor-debug] host floor effect floor=',
          currentFloor,
          'peers=',
          session.peers(),
          'players=',
          dungeonService.getPlayers().map((p) => p.id),
        );
      }
      // A reconnected peer sends from a NEW peerId, but its avatar id and
      // stored loadout key stay the original playerId — resolve before spawning,
      // else the floor-change re-spawn misses the loadout and drops the client.
      for (const peerId of session.peers()) {
        dungeonService.spawnRemotePlayer(
          reconnectRegistry.resolvePlayerId(peerId),
        );
      }
    }
  }, [mapSize, currentFloor, activeRunConfig]);

  useEffect(() => {
    mapService.onTilesChanged = () => {
      setFloorTiles([...mapService.getMapTiles()]);
    };
    // Host: replicate runtime tile changes (challenge-room seal/restore) — a client
    // generates no floor and never seals, so without this its walls never appear.
    mapService.onTilesMutated = (deltas) => {
      if (netService.isHost()) {
        netService.session?.broadcast('reliable', makeTileMutation(deltas));
      }
    };
    // Host: replicate a runtime location spawn (challenge reward chest) — same
    // reason; the client never runs the challenge sim that places it.
    dynamicPlacementService.onLocationSpawned = (loc) => {
      if (netService.isHost()) {
        netService.session?.broadcast(
          'reliable',
          makeWorldUpdate([{ op: 'add', location: loc }]),
        );
      }
    };
    return () => {
      mapService.onTilesChanged = () => {
        //
      };
      mapService.onTilesMutated = () => {
        //
      };
      dynamicPlacementService.onLocationSpawned = () => {
        //
      };
    };
  }, []);

  // The camera container only exists once assets are loaded. On a dungeon join
  // it can mount after `applyFloorSwap` ran, and the render loop is paused
  // during the cover — so position it as soon as it appears, otherwise the
  // reveal would show the origin instead of the player.
  useEffect(() => {
    if (isLoaded) {
      applyCameraTransform();
    }
  }, [isLoaded, applyCameraTransform]);

  useEffect(() => {
    const endRun = () => {
      SocketService.clearRunModifiers();
      saveService.clearRunSave();
      closeInterface();
      hubService.enterHub('from-dungeon-exit');
    };

    dungeonService.onGameEvent = (type, payload) => {
      const floorsCount = dungeonService.getCurrentDungeonFloorCount();
      if (type === 'nextFloor') {
        const currentFloor = useGameStore.getState().currentFloor;
        if (currentFloor < floorsCount) {
          const player = dungeonService.getPlayer();
          startFloorChange(currentFloor + 1, player ?? undefined);
        } else {
          const dungeonId = useGameStore.getState().currentDungeonId;
          if (dungeonId) {
            onDungeonCompleted(dungeonId);
          }
          resetCoopDeath();
          endRun();
        }
      } else if (type === 'goToFloor') {
        const target = (payload as { floor?: number } | undefined)?.floor ?? 0;
        if (target >= 1 && target <= floorsCount) {
          const player = dungeonService.getPlayer();
          startFloorChange(target, player ?? undefined);
        } else {
          endRun();
        }
      } else if (type === 'returnToHub') {
        endRun();
      } else if (type === 'defeat') {
        SocketService.clearRunModifiers();
        recoveryService.applyDefeatRecovery();
        saveService.clearRunSave();
        closeInterface();
        hubService.enterHub('from-defeat');
      } else if (type === 'coop-wipe') {
        resetCoopDeath();
        SocketService.clearRunModifiers();
        recoveryService.applyDefeatRecovery();
        saveService.clearRunSave();
        closeInterface();
        netService.session?.setPhase('hub');
        void hubService.enterHub('from-defeat');
      }
    };
    return () => {
      dungeonService.onGameEvent = () => {
        //
      };
    };
  }, [startFloorChange, setGameplay]);

  useDungeonInput();

  useTick((delta) => {
    const deltaTime = delta;
    // While reconnecting / waiting for the host, the session is torn down
    // (role 'offline') — freeze everything. Otherwise the offline branch below
    // would run a full local sim (monsters moving/attacking) behind the overlay.
    if (useReconnectStore.getState().reconnecting) {
      return;
    }
    if (
      netService.role !== 'offline' &&
      useCoopRunStore.getState().phase !== 'running'
    ) {
      return;
    }
    const cinematicActive = useCinematicStore.getState().active;

    if (netService.isClient()) {
      const session = netService.session;
      const localPlayer = dungeonService.getLocalPlayer();
      if (localPlayer && localPlayer.id === dungeonService.localPlayerId) {
        localPlayer.tickPrediction(deltaTime);
      }
      // Local re-pick gating only; the host is authoritative for actual pickup.
      dungeonService.inventoryService.armCommonItems();
      animationService.updateSpellAnimations(deltaTime);
      projectileService.tickClientProjectiles(deltaTime);
      if (session && replicationClientRef.current) {
        replicationClientRef.current.interpolate(session.clientHostNow());
      }
    } else if (!cinematicActive) {
      // A floor transition freezes the shared world (mid-run swap + the 'enter'
      // cover wrapping the initial load / a host resume): no monster move / AI /
      // attack / spawn while the cover is up. Combat is host-authoritative, so
      // freezing the sim here stops every hit for the party; replication keeps
      // running so clients see frozen entities, not an empty floor. (Solo also
      // stops its ticker via isPaused — this also covers the 'swap' fade-in.)
      if (!useFloorTransitionStore.getState().active) {
        const entities = dungeonService.getEntities();
        entities.forEach((entity) => {
          entity.tick(deltaTime);
          if (entity instanceof MonsterEntity && entity.canCast()) {
            entity.executeAI();
          } else if (entity instanceof PlayerEntity) {
            // Host: drain a queued client move (no-op for the host's own local
            // player, whose input is applied directly, and offline).
            entity.processPendingMove();
          }
        });
        dungeonService.monsterSpawningService.updateSpawner(deltaTime);
        dungeonService.inventoryService.armCommonItems();
        animationService.updateSpellAnimations(deltaTime);
        projectileService.update(deltaTime);
        zoneEffectService.tick(deltaTime);
        dynamicPlacementService.tick();
      }
      if (netService.isHost() && replicationHostRef.current) {
        replicationHostRef.current.tick();
      }
    }

    cinematicService.tick(deltaTime);

    const lockCameraPosition = useGameStore.getState().lockCameraPosition;
    const cameraTarget = dungeonService.cameraTargetEntity();
    if (cameraTarget && !lockCameraPosition) {
      const smooth = smoothCameraRef.current;
      if (dungeonService.consumeCameraSnap()) {
        smooth.x = cameraTarget.absoluteX;
        smooth.y = cameraTarget.absoluteY;
      } else {
        smooth.x +=
          (cameraTarget.absoluteX - smooth.x) * CAMERA_LERP_SPEED * deltaTime;
        smooth.y +=
          (cameraTarget.absoluteY - smooth.y) * CAMERA_LERP_SPEED * deltaTime;
      }
      setCameraPosition({ x: smooth.x, y: smooth.y });
    }

    applyCameraTransform();

    if (dungeonService.shouldRender()) {
      dungeonService.clearDirtyFlags();
      setTick((prev) => prev + 1);
      setEntityTick(entityTick + 1);
    }
  });

  return (
    <>
      {!isLoaded ? (
        <>
          <Text
            text={'Chargement des assets'}
            anchor={1}
            x={screenWidth / 2}
            y={screenHeight / 2}
            zIndex={300}
            style={
              {
                fontSize: 8 * zoomFactor,
                fill: 'white',
                fontWeight: 'bold',
              } as any
            }
          />
        </>
      ) : (
        <Container ref={cameraContainerRef} sortableChildren={true}>
          <Environment
            tiles={floorTiles}
            mapWidth={mapSize.width}
            mapHeight={mapSize.height}
            tilesetMapping={
              floorTilesets[currentTilesetKey] ?? DEFAULT_TILESET_MAPPING
            }
          />

          <LocationRenderer />

          {dungeonService.getEntities().map((entity) => {
            return (
              <EntityRenderer
                key={entity.id}
                entity={entity}
                renderVersion={entity.renderVersion}
              />
            );
          })}

          {/* Direct child of cameraContainer (not the overlay) so projectiles
              Y-sort with entities — passing behind / in front by depth. */}
          <ProjectileRenderer />

          <GroundItemsRenderer />

          {/* Direct child of cameraContainer so its zIndex sits just above the
              ground tiles — i.e. below walls/water, locations, items and entities. */}
          <AffectedTilesOverlayAlone />

          {/* Overlays (spells, aim) must always sit above terrain and entities,
              hence a wrapping Container at a constant high zIndex. */}
          <Container zIndex={getZIndex({ type: 'overlay' })}>
            <SpellAnimationsRenderer />
            <EffectsPreloader />
            {dungeonService.getPlayer() && (
              <AimingOverlay
                player={dungeonService.getPlayer()}
                isVisible={false}
              />
            )}
          </Container>

          {lockCameraPosition && (
            <Sprite
              anchor={[0.5, 0.5]}
              texture={Texture.from('dev.png')}
              width={tileWidth}
              height={tileHeight}
              scale={zoomFactor}
              position={[
                smoothCameraRef.current.x * zoomFactor,
                -smoothCameraRef.current.y * zoomFactor,
              ]}
            />
          )}
        </Container>
      )}
    </>
  );
};

export default Dungeon;
